import { useSearchPageState } from '@klareospace/pharos-lib-front-utils';
import type { SortDirection, SpaceWithSiteCount } from '@klareospace/pharos-lib-types';

import { useSpaces } from './use-spaces';
import type { SpaceSortableColumn } from '../types/space.types';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface UseSpacesPageOptions {
  enabled?: boolean;
}

export interface UseSpacesPageReturn {
  currentPage: number;
  error: Error | null;
  isLoading: boolean;
  onPageChange: (newPage: number) => void;
  onSearchChange: (value: string) => void;
  onSort: (sortBy: SpaceSortableColumn, sortDirection: SortDirection | null) => void;
  pageSize: number;
  searchValue: string;
  sortBy: SpaceSortableColumn | undefined;
  sortDirection: SortDirection | undefined;
  spaces: SpaceWithSiteCount[];
  totalItems: number;
  totalPages: number;
}

// ─── Hook ─────────────────────────────────────────────────────────────────────

export function useSpacesPage(options?: UseSpacesPageOptions): UseSpacesPageReturn {
  const searchState = useSearchPageState<SpaceSortableColumn>();

  const { data, error, isLoading } = useSpaces({
    enabled: options?.enabled,
    name: searchState.debouncedSearch || undefined,
    page: searchState.currentPage,
    pageSize: searchState.pageSize,
    sort: searchState.sort,
  });

  return {
    currentPage: searchState.currentPage,
    error: error ?? null,
    isLoading,
    onPageChange: searchState.onPageChange,
    onSearchChange: searchState.onSearchChange,
    onSort: searchState.onSort,
    pageSize: searchState.pageSize,
    searchValue: searchState.searchValue,
    sortBy: searchState.sortBy,
    sortDirection: searchState.sortDirection,
    spaces: data?.items ?? [],
    totalItems: data?.total ?? 0,
    totalPages: data?.totalPages ?? 1,
  };
}
